/**
 * SAAS-PORTAL — gestão de usuários do próprio tenant (FASE 6).
 *
 * Para tenant_owner/tenant_admin: listar, criar (papéis limitados a
 * tenant_admin/tenant_operator/viewer), ativar/desativar e resetar senha dos
 * usuários da PRÓPRIA empresa. O backend força o tenant e recusa papéis de
 * plataforma (super_admin/field) e edição do dono. A senha temporária é
 * exibida UMA vez.
 */
import { useCallback, useEffect, useState } from "react";
import NavIcon from "../nav/NavIcon.jsx";
import {
  portalListUsers,
  portalCreateUser,
  portalUpdateUser,
  portalResetUserPassword,
} from "../lib/api.js";

const ASSIGNABLE = [
  { value: "tenant_admin", label: "Administrador" },
  { value: "tenant_operator", label: "Operador" },
  { value: "viewer", label: "Visualizador" },
];

function roleLabel(role) {
  if (role === "tenant_owner") return "Dono";
  return ASSIGNABLE.find((r) => r.value === role)?.label || role;
}

export default function PortalUsersPage() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [tempInfo, setTempInfo] = useState(null); // {email, temp_password}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = await portalListUsers();
      setUsers(out.users || []);
    } catch (err) {
      setError(err?.message || "Falha ao carregar usuários.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setError(null);
    setTempInfo(null);
    if (!email.trim()) { setError("Informe o e-mail."); return; }
    setBusy(true);
    try {
      const out = await portalCreateUser({
        email: email.trim(),
        full_name: fullName.trim() || undefined,
        role,
      });
      setTempInfo({ email: out.email, temp_password: out.temp_password });
      setEmail(""); setFullName(""); setRole("viewer");
      await load();
    } catch (err) {
      setError(err?.message || "Falha ao criar usuário.");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggle(u) {
    setError(null);
    try {
      await portalUpdateUser(u.id, { is_active: !u.is_active });
      await load();
    } catch (err) {
      setError(err?.message || "Falha ao atualizar usuário.");
    }
  }

  async function handleReset(u) {
    setError(null); setTempInfo(null);
    try {
      const out = await portalResetUserPassword(u.id);
      setTempInfo({ email: out.email, temp_password: out.temp_password });
    } catch (err) {
      setError(err?.message || "Falha ao resetar senha.");
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Acesso da empresa</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">Usuários da empresa</h1>
          <p className="text-sm text-noc-muted mt-1 max-w-2xl">
            Convide pessoas e defina o nível de acesso ao portal com segurança.
          </p>
        </div>
        <button onClick={load} className="btn-ghost h-10 px-4 inline-flex items-center gap-2">
          <NavIcon name="refresh" size={15} />
          Atualizar
        </button>
      </header>

      {error && (
        <div className="flex items-center gap-2 text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg px-3 py-2">
          <span className="led-err" />{error}
        </div>
      )}

      {tempInfo && (
        <div className="text-sm text-noc-text bg-noc-accent/10 border border-noc-accent/30 rounded-lg px-3 py-2">
          Senha temporária de <span className="font-mono">{tempInfo.email}</span>:{" "}
          <span className="font-mono font-semibold select-all">{tempInfo.temp_password}</span>
          <div className="text-[11px] text-noc-muted mt-1">
            Copie agora — ela não será exibida novamente. Peça ao usuário para trocá-la no primeiro acesso.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="stat-card">
          <div className="section-label">Usuários</div>
          <div className="text-2xl font-semibold mt-2">{users?.length ?? "—"}</div>
        </div>
        <div className="stat-card">
          <div className="section-label">Ativos</div>
          <div className="text-2xl font-semibold text-noc-ok mt-2">
            {users ? users.filter((u) => u.is_active).length : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="section-label">Administradores</div>
          <div className="text-2xl font-semibold text-noc-accent mt-2">
            {users ? users.filter((u) => u.role === "tenant_admin" || u.is_owner).length : "—"}
          </div>
        </div>
      </div>

      <section className="glass-card p-5">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-noc-text">Convidar novo usuário</h2>
          <p className="text-xs text-noc-muted mt-1">O acesso será criado com senha temporária exibida uma única vez.</p>
        </div>
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div className="space-y-1 sm:col-span-2">
          <label className="label">E-mail</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
            placeholder="usuario@empresa.com" />
        </div>
        <div className="space-y-1">
          <label className="label">Nome</label>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)}
            className="w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
            placeholder="opcional" />
        </div>
        <div className="space-y-1">
          <label className="label">Papel</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full h-9 px-2 rounded-lg bg-noc-bg border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none">
            {ASSIGNABLE.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-4">
          <button type="submit" disabled={busy} className="btn-primary h-10 px-4 inline-flex items-center gap-2">
            <NavIcon name="users" size={15} />
            {busy ? "Criando…" : "Convidar usuário"}
          </button>
        </div>
        </form>
      </section>

      <section className="glass-card !p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-noc-divider">
          <h2 className="text-sm font-semibold text-noc-text">Pessoas com acesso</h2>
          <p className="text-xs text-noc-muted mt-0.5">Papéis e status atuais dos usuários desta empresa.</p>
        </div>
        <div className="divide-y divide-noc-divider">
        {loading && <div className="p-4 text-sm text-noc-muted">Carregando…</div>}
        {!loading && users && users.length === 0 && (
          <div className="p-4 text-sm text-noc-muted">Nenhum usuário ainda.</div>
        )}
        {!loading && users && users.map((u) => (
          <div key={u.id} className="px-5 py-4 flex items-center gap-3 flex-wrap hover:bg-noc-panel2/30 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-noc-text truncate">
                {u.full_name || u.email}
                {u.is_owner && (
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-noc-accent">dono</span>
                )}
              </div>
              <div className="text-[11px] text-noc-muted font-mono truncate">{u.email}</div>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full border border-noc-border text-noc-text bg-noc-bg/30">
              {roleLabel(u.role)}
            </span>
            <span className={`text-xs ${u.is_active ? "text-noc-ok" : "text-noc-muted"}`}>
              {u.is_active ? "ativo" : "inativo"}
            </span>
            {!u.is_owner && (
              <div className="flex gap-2">
                <button onClick={() => handleToggle(u)} className="btn-ghost h-7 px-2 text-xs">
                  {u.is_active ? "Desativar" : "Ativar"}
                </button>
                <button onClick={() => handleReset(u)} className="btn-ghost h-7 px-2 text-xs">
                  Resetar senha
                </button>
              </div>
            )}
          </div>
        ))}
        </div>
      </section>
    </div>
  );
}
